import { X } from "lucide-react";
import React from "react";

/**
 * NudgeBanner — the shared "nudge" surface from the design system.
 *
 * One dismissible prompt tied to a real next action: onboarding hints,
 * personalized recommendations, progress/completion celebrations. Colours come
 * from the design tokens (styles/tokens.css), so the PWA and the website render
 * it identically and a token change updates every instance.
 *
 * Rules of use (keep it from nagging): show one at a time, tie it to an action,
 * always allow dismissal, and use the "success" variant to celebrate completion.
 */

export type NudgeVariant = "info" | "success" | "warning" | "personalized";

interface VariantTokens {
  bg: string;
  border: string;
  tagBg: string;
}

const VARIANTS: Record<NudgeVariant, VariantTokens> = {
  info: {
    bg: "var(--indigo-50)",
    border: "var(--indigo-200)",
    tagBg: "var(--indigo-600)",
  },
  personalized: {
    bg: "var(--indigo-50)",
    border: "var(--indigo-200)",
    tagBg: "var(--indigo-600)",
  },
  success: {
    bg: "var(--ready-50)",
    border: "var(--ready-500)",
    tagBg: "var(--ready-600)",
  },
  // Tangerine is the "next step" colour. The tag carries white text, so it
  // takes the 600 step; 500 is a fill for ink text only.
  warning: {
    bg: "var(--tangerine-50)",
    border: "var(--tangerine-500)",
    tagBg: "var(--tangerine-600)",
  },
};

interface NudgeBannerProps {
  /** Colour/intent of the nudge. Defaults to "info". */
  variant?: NudgeVariant;
  /** Short uppercase label shown as a pill (e.g. "Nudge", "Personalized"). */
  tag?: string;
  /** Leading icon — a lucide icon element sized ~18px. */
  icon?: React.ReactNode;
  /** Bold lead line. */
  title?: React.ReactNode;
  /** Supporting copy / the action. */
  children?: React.ReactNode;
  /** When provided, renders a dismiss button that calls this. */
  onDismiss?: () => void;
  /**
   * Whether this is a live region. True (default) suits a dynamic nudge that
   * appears in response to something — an error, a state change — where a
   * screen reader should announce it. Set false for an always-present static
   * note so it is exposed as a plain note and not announced as a live update.
   */
  live?: boolean;
  className?: string;
}

export const NudgeBanner: React.FC<NudgeBannerProps> = ({
  variant = "info",
  tag,
  icon,
  title,
  children,
  onDismiss,
  live = true,
  className = "",
}) => {
  const v = VARIANTS[variant];

  return (
    <div
      role={live ? "status" : "note"}
      aria-live={live ? "polite" : undefined}
      className={`relative flex items-start gap-3 rounded-xl border p-3.5 text-sm leading-snug ${className}`}
      style={{ background: v.bg, borderColor: v.border }}
    >
      {tag && (
        <span
          className="absolute -top-2 left-3.5 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white"
          style={{ background: v.tagBg }}
        >
          {tag}
        </span>
      )}
      {icon && <span className="mt-0.5 flex-shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        {title && <span className="font-bold">{title} </span>}
        {children}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 -mt-1 flex-shrink-0 rounded-md p-1 text-slate-400 hover:bg-black/5 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
